import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import type { EventEmitter } from 'eventemitter3';
import { type JSError, normalizeError } from '@epihub/core/src/errors';
import { Emitter } from '@epihub/core/src/events';
import { uuidv4 } from '@epihub/core/src/fn/uuidv4';
import { log } from '@epihub/core/src/_ctx/log';

export function bindEmitterToWSClient(params: {
  url: string;
  path: string;
  onError?: (error: JSError) => void;
  getCredentials?: () => Promise<object>;
}) {
  const socket = io(params.url, {
    path: params.path,
    autoConnect: false,
    transports: [
      // 'polling',
      'websocket',
    ],
    auth: async (cb) => {
      const credentials = await params.getCredentials?.();
      cb(credentials ?? {});
    },
    // secure: true,
  }) as Socket<
    {
      serverToClient(p: { subId: string; event: string; args: object[] }): void;
      serverToClientError(p: { error: ReturnType<JSError['toJSON']> }): void;
    },
    {
      subscribe(p: { subId: string; event: string }): void;
      unsubscribe(p: { subId: string; event: string }): void;
    }
  >;

  const subscriptions = {} as {
    [subId: string]: {
      event: string;
      handler: EventEmitter.EventListener<any, any>;
    };
  };

  function findSubscriptionFromHandler(handlerToFind: EventEmitter.EventListener<any, any>) {
    for (const [subId, { event, handler }] of Object.entries(subscriptions)) {
      if (handler === handlerToFind) {
        return {
          subId,
          event,
        };
      }
    }
    return null;
  }

  function subscribe(subId: string, event: string) {
    socket.emit('subscribe', { subId, event: String(event) });
    log.i('bindEmitterToWSClient::subscribe', `subscribing to ${event}`);
  }

  class ClientEventEmitter extends Emitter {
    constructor() {
      super();
      this.addListener = this.addListener.bind(this);
      this.removeListener = this.removeListener.bind(this);
      this.on = this.addListener.bind(this);
      this.off = this.removeListener.bind(this);

      this.emit = this.emit.bind(this);
      this.once = this.once.bind(this);
      this.removeAllListeners = this.removeAllListeners.bind(this);
      this.listen = this.listen.bind(this);
      this.send = this.send.bind(this);
    }

    addListener(
      event: string,
      fn: (data: object) => void,
      context?: any,
    ): this {
      const subId = uuidv4();
      this.removeListener(event, fn);
      subscribe(subId, String(event));

      subscriptions[subId] = {
        handler: fn,
        event: String(event),
      };

      return super.addListener(event, fn, context);
    }

    removeListener(event: string, fn?: (data: object) => void, context?: any, once?: boolean): this {
      if (fn) {
        const sub = findSubscriptionFromHandler(fn);
        if (sub && subscriptions[sub.subId] && subscriptions[sub.subId].event === String(event)) {
          // Inform the server about the unsubscription
          socket.emit('unsubscribe', { subId: sub.subId, event: sub.event });

          // Remove the subscription record locally
          delete subscriptions[sub.subId];
        }
        return super.removeListener(event, fn, context, once);
      }

      // Case when no function is provided, implying all subscriptions for the event should be removed
      Object.entries(subscriptions).forEach(([subId, { event: subscribedEvent }]) => {
        if (subscribedEvent === String(event)) {
          // Inform the server about the unsubscription
          socket.emit('unsubscribe', { subId, event: subscribedEvent });

          // Remove the subscription record locally
          delete subscriptions[subId];
        }
      });

      return super.removeListener(event, fn, context, once);
    }

    once(event: string, fn: (data: object) => void, context?: any): this {
      // @ts-expect-error
      const wrappedFn: EventEmitter.EventListener<EventTypes, T> = (...args) => {
        this.removeListener(event, wrappedFn, context);
        // @ts-expect-error
        fn.apply(context, args);
      };

      return this.on(event, wrappedFn, context);
    }

    removeAllListeners(event?: string): this {
      if (event) {
        const subs = Object.entries(subscriptions).filter(([, sub]) => sub.event === event);
        subs.forEach(([subId]) => {
          socket.emit('unsubscribe', { subId, event: String(event) });
          delete subscriptions[subId];
        });
      } else {
        Object.entries(subscriptions).forEach(([subId, sub]) => {
          socket.emit('unsubscribe', { subId, event: sub.event });
          delete subscriptions[subId];
        });
      }

      return super.removeAllListeners(event);
    }
  }

  const emitter = new ClientEventEmitter();

  socket.on('connect', () => {
    log.i('eventEmitterAsWSServer::connect', `to ${socket.id}`);

    socket.on('serverToClient', ({ event, args }) => {
      emitter.emit(event, ...args);
    });

    socket.on('serverToClientError', ({ error }) => {
      params.onError?.(normalizeError(error));
    });

    // This could be a reconnect, in that case
    // subscriptions will already be populated
    for (const [subId, sub] of Object.entries(subscriptions)) {
      subscribe(subId, sub.event);
    }
  });

  return {
    socket,
    emitter,
    connect: () => socket.connect(),
    disconnect: () => socket.disconnect(),
    reconnect: () => {
      socket.disconnect();
      socket.connect();
    },
  };
}
